'use client';

import { toTraceWriteFileEntry } from '@terra/shared-chat-trace/trace-view/utils/write-file-tool';
import { Box, HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Collapsible } from '../../../../components/collapsible';
import { IconButton } from '../../../../components/icon-button';
import { Tooltip } from '../../../../components/tooltip';
import { MetaDefault } from '../../../../components/typography';
import { type TraceToolCall } from '../types';
import { formatTruncatedFilePath } from '../utils/read-file-tool';
import {
  TRACE_INLINE_LABEL_COLOR,
  TRACE_INLINE_ROW_GAP,
  TRACE_INLINE_ROW_MIN_HEIGHT,
  TRACE_INLINE_SUMMARY_COLOR,
} from '../utils/trace-inline-layout';
import { TraceExpandChevron } from './TraceExpandChevron';
import { TraceReadFileContent } from './TraceReadFileContent';

const WRITE_FILE_CONTENT_MAX_HEIGHT = '360px';

type WriteFileToolCardProps = {
  toolCall: TraceToolCall;
};

export function WriteFileToolCard({ toolCall }: WriteFileToolCardProps) {
  const t = useTranslations('designSystem.chat.writeTool');
  const [isOpen, setIsOpen] = useState(false);
  const entry = toTraceWriteFileEntry(toolCall);
  const pathLabel = entry.filePath
    ? formatTruncatedFilePath({ filePath: entry.filePath })
    : t('unknownFile');

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
      width="100%"
    >
      <HStack
        w="full"
        gap={TRACE_INLINE_ROW_GAP}
        alignItems="center"
        minW={0}
        minHeight={TRACE_INLINE_ROW_MIN_HEIGHT}
      >
        <MetaDefault
          color={TRACE_INLINE_LABEL_COLOR}
          flexShrink={0}
          whiteSpace="nowrap"
        >
          {t('label')}
        </MetaDefault>
        <MetaDefault color={TRACE_INLINE_SUMMARY_COLOR} flexShrink={0}>
          &middot;
        </MetaDefault>
        <MetaDefault
          color={TRACE_INLINE_SUMMARY_COLOR}
          fontFamily="mono"
          minW={0}
          flexShrink={1}
          truncate
          title={entry.filePath}
        >
          {pathLabel}
        </MetaDefault>
        {entry.content ? (
          <Tooltip title={isOpen ? t('hideContent') : t('viewContent')}>
            <Collapsible.Trigger asChild>
              <IconButton
                size="xs"
                variant="ghost"
                flexShrink={0}
                color={isOpen ? 'fg.default' : 'fg.subtle'}
                aria-label={isOpen ? t('hideContent') : t('viewContent')}
              >
                <TraceExpandChevron isExpanded={isOpen} size={12} />
              </IconButton>
            </Collapsible.Trigger>
          </Tooltip>
        ) : null}
      </HStack>

      <Collapsible.Content>
        <Box pt={1} pb={2} width="100%">
          <TraceReadFileContent
            filePath={entry.filePath}
            output={entry.content}
            isMarkdown={entry.isMarkdown}
            maxHeight={WRITE_FILE_CONTENT_MAX_HEIGHT}
          />
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
